import React, { useState, useRef } from 'react';
import {
  Dialog, DialogContent, Typography, Button, Box,
} from '@mui/material';
import { QRCodeSVG } from 'qrcode.react';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { useQRLogo } from '../../../hooks/useQRLogo';

const QR_SIZE   = 260;
const LOGO_SIZE = 40;

export default function QRDialog({ url, title, open, onClose }) {
  const qrLogo     = useQRLogo();
  const svgWrapRef = useRef();
  const [copied, setCopied] = useState(false);

  if (!url) return null;

  async function handleCopyQR() {
    const svgEl = svgWrapRef.current?.querySelector('svg');
    if (!svgEl) return;
    try {
      const svgClone = svgEl.cloneNode(true);
      svgClone.querySelectorAll('image').forEach(el => el.remove());
      const svgData = new XMLSerializer().serializeToString(svgClone);

      const canvas = document.createElement('canvas');
      canvas.width  = QR_SIZE;
      canvas.height = QR_SIZE;
      const ctx = canvas.getContext('2d');

      const qrBlob    = new Blob([svgData], { type: 'image/svg+xml' });
      const qrBlobUrl = URL.createObjectURL(qrBlob);
      await new Promise((resolve, reject) => {
        const img = new window.Image();
        img.onload = () => {
          ctx.fillStyle = '#fff';
          ctx.fillRect(0, 0, QR_SIZE, QR_SIZE);
          ctx.drawImage(img, 0, 0, QR_SIZE, QR_SIZE);
          URL.revokeObjectURL(qrBlobUrl);
          resolve();
        };
        img.onerror = reject;
        img.src = qrBlobUrl;
      });

      if (qrLogo) {
        await new Promise(resolve => {
          const logoImg = new window.Image();
          logoImg.crossOrigin = 'anonymous';
          logoImg.onload = () => {
            const cx = QR_SIZE / 2;
            const cy = QR_SIZE / 2;
            const pad = 5;
            ctx.fillStyle = '#fff';
            ctx.fillRect(cx - LOGO_SIZE / 2 - pad, cy - LOGO_SIZE / 2 - pad, LOGO_SIZE + pad * 2, LOGO_SIZE + pad * 2);
            ctx.drawImage(logoImg, cx - LOGO_SIZE / 2, cy - LOGO_SIZE / 2, LOGO_SIZE, LOGO_SIZE);
            resolve();
          };
          logoImg.onerror = resolve;
          logoImg.src = qrLogo;
        });
      }

      await new Promise((resolve, reject) => {
        canvas.toBlob(async (pngBlob) => {
          try {
            await navigator.clipboard.write([new ClipboardItem({ 'image/png': pngBlob })]);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
            resolve();
          } catch (e) { reject(e); }
        }, 'image/png');
      });
    } catch {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, gap: 2 }}>
        <Typography fontWeight={700} textAlign="center">{title || 'Delningslänk'}</Typography>
        <Box ref={svgWrapRef} sx={{ bgcolor: '#fff', p: 2.5, borderRadius: 2, display: 'inline-flex' }}>
          <QRCodeSVG
            value={url}
            size={QR_SIZE}
            level="H"
            imageSettings={qrLogo ? {
              src: qrLogo,
              width: LOGO_SIZE,
              height: LOGO_SIZE,
              excavate: true,
            } : undefined}
          />
        </Box>
        <Typography variant="caption" color="text.secondary"
          sx={{ fontFamily: 'monospace', wordBreak: 'break-all', textAlign: 'center' }}>
          {url}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained" size="small"
            startIcon={copied ? <CheckIcon sx={{ fontSize: 16 }} /> : <ContentCopyIcon sx={{ fontSize: 16 }} />}
            onClick={handleCopyQR}
            sx={{ textTransform: 'none', bgcolor: copied ? 'success.main' : '#1a1a1a', '&:hover': { bgcolor: copied ? 'success.dark' : '#333' } }}
          >
            {copied ? 'Kopierad!' : 'Kopiera QR-bild'}
          </Button>
          <Button variant="outlined" size="small" onClick={onClose} sx={{ textTransform: 'none' }}>Stäng</Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
